import { useState } from "react";
import { useMeters, useLoadProfile } from "../hooks/useMeters";
import {
  PageHeader,
  ChartCard,
  MeterSelect,
  DaysSelect,
  Spinner,
} from "../components/common";
import { DAY_LABELS } from "../utils/helpers";
import Layout from "../../../layout";
import "../reportAnalytics.css";

const HOURS = Array.from({ length: 24 }, (_, i) => i);

function heatColor(value: number, max: number): string {
  if (max === 0) return "#1E293B";
  const ratio = value / max;
  if (ratio < 0.25) return `rgba(59,130,246,${0.2 + ratio * 1.5})`;
  if (ratio < 0.6) return `rgba(6,182,212,${0.4 + ratio})`;
  return `rgba(245,158,11,${0.5 + ratio * 0.5})`;
}

export default function LoadProfilePage() {
  const { data: meterList } = useMeters();
  const meters = meterList?.meters ?? [];
  const [meterId, setMeterId] = useState(meters[0] ?? "");
  const [days, setDays] = useState(90);
  const [hoveredCell, setHoveredCell] = useState<{
    day: string;
    hour: number;
    value: number;
    x: number;
    y: number;
  } | null>(null);

  const { data, isLoading } = useLoadProfile(meterId || meters[0], days);
  const cells = data?.data ?? [];

  const lookup: Record<number, Record<number, number>> = {};
  cells.forEach((c) => {
    if (!lookup[c.day_of_week]) lookup[c.day_of_week] = {};
    lookup[c.day_of_week][c.hour] = c.avg_kw;
  });
  const max = Math.max(...cells.map((c) => c.avg_kw), 1);

  return (
    <Layout>
      <div className="p-0.5">
        <PageHeader
          title="Load Profile"
          subtitle="Average active power by hour-of-day and day-of-week"
        >
          <MeterSelect
            meters={meters}
            value={meterId || meters[0]}
            onChange={setMeterId}
          />
          <DaysSelect
            value={days}
            onChange={setDays}
            options={[14, 30, 60, 90]}
          />
        </PageHeader>

        <ChartCard
          title="Heatmap: Avg KW"
          subtitle="Brighter = higher average consumption"
        >
          {isLoading ? (
            <Spinner />
          ) : (
            <div className="overflow-x-auto relative">
              <table className="w-full border-separate border-spacing-0.5 text-xs">
                <thead>
                  <tr>
                    <th className="text-slate-600 text-right pr-2 pb-1 font-normal w-10">
                      Day
                    </th>
                    {HOURS.map((h) => (
                      <th
                        key={h}
                        className="text-slate-600 font-normal pb-1 text-center"
                        style={{ minWidth: 28 }}
                      >
                        {h % 6 === 0 ? `${String(h).padStart(2, "0")}h` : ""}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {DAY_LABELS.map((day, dow) => (
                    <tr key={dow}>
                      <td className="text-slate-500 text-right pr-2 py-0.5">
                        {day}
                      </td>
                      {HOURS.map((h) => {
                        const val = lookup[dow]?.[h] ?? 0;
                        return (
                          <td
                            key={h}
                            className="rounded cursor-default"
                            style={{
                              backgroundColor: heatColor(val, max),
                              height: 22,
                              minWidth: 28,
                            }}
                            onMouseEnter={(e) =>
                              setHoveredCell({
                                day,
                                hour: h,
                                value: val,
                                x: e.clientX,
                                y: e.clientY,
                              })
                            }
                            onMouseMove={(e) =>
                              setHoveredCell((prev) =>
                                prev
                                  ? {
                                      ...prev,
                                      x: e.clientX,
                                      y: e.clientY,
                                    }
                                  : prev,
                              )
                            }
                            onMouseLeave={() => setHoveredCell(null)}
                          />
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>

              {hoveredCell && (
                <div
                  className="ra-tooltip pointer-events-none fixed z-[9999]"
                  style={{
                    left: hoveredCell.x + 12,
                    top: hoveredCell.y + 12,
                  }}
                >
                  <div className="font-medium">
                    {hoveredCell.day} {String(hoveredCell.hour).padStart(2, "0")}
                    :00
                  </div>
                  <div className="text-slate-600">
                    {(meterId || meters[0] || "Meter")} -{" "}
                    {hoveredCell.value.toFixed(1)} W
                  </div>
                </div>
              )}

              {/* Legend */}
              <div className="flex items-center gap-3 mt-4 text-xs text-slate-500">
                <span>Low</span>
                <div
                  className="flex-1 h-2 rounded-full"
                  style={{
                    background:
                      "linear-gradient(to right, rgba(59,130,246,0.3), rgba(6,182,212,0.8), rgba(245,158,11,1))",
                  }}
                />
                <span>High ({max.toFixed(1)} W)</span>
              </div>
            </div>
          )}
        </ChartCard>
      </div>
    </Layout>
  );
}
